import React from 'react';
import { useRoute, Link } from 'wouter';
import { useQuery } from '@apollo/client';
import * as PayoutsQuery from 'graphql/payout.graphql';
import { currencyFormatter, dateFormat } from 'utils';
import useCurrentOrganization from 'hooks/useCurrentOrganization';
import ICONS from 'components/Icons';
import LoggedPageContainer from 'components/LoggedPageContainer';
import Summary from '../Details/Summary';
import Table from '~/components/Table';
import Tag from 'components/Tag';
import { PayoutsBalanceByOrganization, PayoutDetails, PendingPayoutBalanceBreakdown } from 'api/data/payout/types';
import './style.scss';

const AvailableSoon = () => {
  const [, params] = useRoute('/:currentSpace/payouts/available-to-pay-out-soon');
  const { currentOrg } = useCurrentOrganization();

  const { data: pendingTx } = useQuery<{ pendingPayoutTransactions: PayoutDetails }>(
    PayoutsQuery.PendingPayoutTransactions,
    {
      variables: {
        availableNow: false,
      },
      fetchPolicy: 'network-only',
    },
  );

  const spaces = pendingTx?.pendingPayoutTransactions.spaces || [];

  const { loading: loadingBalance, data: balanceData } = useQuery<PayoutsBalanceByOrganization>(
    PayoutsQuery.PayoutsBalanceByOrganization,
    {
      variables: {
        organizationId: currentOrg?.id,
      },
      skip: !currentOrg?.id,
    },
  );

  const balance = balanceData?.payoutsBalanceByOrganization;

  const { loading: loadingPendingBalance, data: pendingBalanceData } = useQuery<PendingPayoutBalanceBreakdown>(
    PayoutsQuery.PayoutPendingBalanceBreakdownByOrganization,
    {
      variables: {
        organizationId: currentOrg?.id,
      },
      skip: !currentOrg?.id,
    },
  );

  const pendingBalance = pendingBalanceData?.payoutPendingBalanceBreakdownByOrganization || [];

  return (
    <LoggedPageContainer
      className="payouts available-pay-out"
      organizationStatus={currentOrg?.status}
      headerChildren={
        <div className="row align-center second-header">
          <Link className="row align-center subtitle-x-small" to={`/${params?.currentSpace}/payouts`}>
            {ICONS['wallet']} Payouts
          </Link>
          <span className="row align-center subtitle-x-small arrow-right">
            {ICONS['arrow_right']} Available to payout soon
          </span>
        </div>
      }>
      <div className="max-width-desktop">
        <div className="row space-between align-center payout-header">
          <div>
            <p className="subtitle-xx-small">Available to payout soon</p>
            {loadingBalance && loadingPendingBalance ? (
              <>Loading...</>
            ) : (
              <span className="row align-center heading-small">
                {currencyFormatter(balance?.pendingAmountCents)}
                {currentOrg?.payoutSchedule && currentOrg.payoutSchedule !== 'MANUAL' && (
                  <Tag label={`Scheduled ${currentOrg.payoutSchedule.toLocaleLowerCase()}`} type="neutral" capitalize />
                )}
              </span>
            )}
          </div>
        </div>
        <div className="summary">
          <p className="subtitle-medium">Balance release dates</p>
          <Table
            data={pendingBalance}
            columns={[
              {
                header: 'Date',
                accessor: releaseDate => dateFormat(releaseDate.availableOn, 'MMM D, YYYY'),
                ellipsis: true,
                showSortIcon: true,
              },
              {
                header: 'Amount',
                accessor: releaseDate => currencyFormatter(releaseDate.amountCents),
                ellipsis: true,
              },
            ]}
            getRowId={item => item.availableOn}
          />
        </div>
        <div className="summary">{spaces.length > 0 && <Summary data={spaces} />}</div>
      </div>
    </LoggedPageContainer>
  );
};

export default AvailableSoon;
